import React from 'react';
import { cloneDeep } from 'lodash';
import { UpdateForm } from 'framework/form';
import { useParams } from 'react-router-dom';
import { ShowSection } from 'framework/ui/layout';
import useBillingService, { BillingRequestSearchResultItem } from '../billingService';
import MigrateBillingRequestForm from './MigrateBillingRequestForm';
import { UpdateMigratedBillingRequestPayload } from '../billingTypes';

const prepareFormData = (billingRequest: BillingRequestSearchResultItem) => {
  const items = billingRequest.items.map((item, index) => ({
    ...cloneDeep(item),
    formItemId: `${item.id}-${index}`,
    milestoneName: item.name,
  }));

  return {
    data: items,
    type: billingRequest.billingType,
    note: billingRequest.note || '',
    artifactId: billingRequest.serviceDetailsArtifactId
      ? [billingRequest.serviceDetailsArtifactId]
      : billingRequest.reimbursementDocumentationArtifactIds || [],
    billingRequestDate: billingRequest.asOfDate || '',
  };
};

const createPayload = (values: any): UpdateMigratedBillingRequestPayload => {
  const rawItems = values.data || [];

  const billedItems = rawItems.map((item: any) => ({
    id: item.id || '',
    scheduleItemId: item.scheduleItemId || '',
    name: item.name || '',
    amount: item.amount || 0,
    currency: item.currency || '',
    ...(item.hours ? { hours: item.hours || 0 } : {}),
  }));

  return {
    note: values.note || '',
    billingRequestArtifactId: values.artifactId?.[0] || undefined,
    billedItems,
    billingRequestDate: values.billingRequestDate || '',
  };
};

const UpdateMigratedBillingRequestLayout = () => {
  const { getBillingRequest, updateMigratedBillingRequest, getMigrateBillingValidationSchema } = useBillingService();
  const { billingScheduleId, billingRequestId } = useParams<Dictionary<string>>();

  const handleGetData = async () => {
    const billingRequest = await getBillingRequest(billingScheduleId!, billingRequestId!);
    return prepareFormData(billingRequest);
  };

  const handleSubmit = async (_requestId: StringOrNumber, values: any) => {
    const payload = createPayload(values);
    return updateMigratedBillingRequest(billingScheduleId!, billingRequestId!, payload);
  };

  return (
    <UpdateForm
      idSource="billingRequestId"
      getDataFunc={handleGetData}
      submitFormFunc={handleSubmit}
      afterSubmit={{
        message: 'You are awesome! Migrated billing request has been successfully updated.',
      }}
      getValidationSchemaFunc={getMigrateBillingValidationSchema}
    >
      <ShowSection title="Update Migrated Billing Request:" titleVariant="h2">
        <MigrateBillingRequestForm submitLabel="Update" />
      </ShowSection>
    </UpdateForm>
  );
};

export default UpdateMigratedBillingRequestLayout;
